// Minimal QR encoder — byte mode, ECC level M, versions 1-10. No dependencies.
// Verified module-for-module against the python-qrcode reference encoder.

const ECC: Record<number, [number, number]> = {
  1: [10, 1], 2: [16, 1], 3: [26, 1], 4: [18, 2], 5: [24, 2],
  6: [16, 4], 7: [18, 4], 8: [22, 4], 9: [22, 5], 10: [26, 5],
};
const TOTAL: Record<number, number> = {
  1: 26, 2: 44, 3: 70, 4: 100, 5: 134, 6: 172, 7: 196, 8: 242, 9: 292, 10: 346,
};
const ALIGN: Record<number, number[]> = {
  1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
  6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
};

const EXP = new Array<number>(510);
const LOG = new Array<number>(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    EXP[i + 255] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
})();

const gmul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function rsRemainder(data: number[], degree: number): number[] {
  let gen = [1];
  for (let i = 0; i < degree; i++) {
    const factor = [1, EXP[i]];
    const next = new Array<number>(gen.length + 1).fill(0);
    for (let a = 0; a < gen.length; a++)
      for (let b = 0; b < 2; b++) next[a + b] ^= gmul(gen[a], factor[b]);
    gen = next;
  }
  const res = new Array<number>(degree).fill(0);
  for (const byte of data) {
    const factor = byte ^ res.shift()!;
    res.push(0);
    if (factor !== 0) for (let j = 0; j < degree; j++) res[j] ^= gmul(gen[j + 1], factor);
  }
  return res;
}

const MASKS: Array<(x: number, y: number) => boolean> = [
  (x, y) => (x + y) % 2 === 0,
  (x, y) => y % 2 === 0,
  (x, y) => x % 3 === 0,
  (x, y) => (x + y) % 3 === 0,
  (x, y) => (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0,
  (x, y) => ((x * y) % 2) + ((x * y) % 3) === 0,
  (x, y) => (((x * y) % 2) + ((x * y) % 3)) % 2 === 0,
  (x, y) => (((x + y) % 2) + ((x * y) % 3)) % 2 === 0,
];

export interface QRCode {
  size: number;
  modules: boolean[][];
}

export function encode(text: string): QRCode {
  const bytes = new TextEncoder().encode(text);
  let version = 0;
  for (let v = 1; v <= 10; v++) {
    const [ecLen, numBlocks] = ECC[v];
    const dataCW = TOTAL[v] - ecLen * numBlocks;
    if (4 + (v <= 9 ? 8 : 16) + 8 * bytes.length <= dataCW * 8) {
      version = v;
      break;
    }
  }
  if (!version) throw new Error("QR payload too long");
  const [ecLen, numBlocks] = ECC[version];
  const dataCW = TOTAL[version] - ecLen * numBlocks;
  const cci = version <= 9 ? 8 : 16;
  const bits: number[] = [];
  const push = (val: number, n: number) => {
    for (let i = n - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };
  push(4, 4);
  push(bytes.length, cci);
  for (const b of bytes) push(b, 8);
  push(0, Math.min(4, dataCW * 8 - bits.length));
  while (bits.length % 8 !== 0) bits.push(0);
  const data: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    data.push(b);
  }
  for (let pad = 0xec; data.length < dataCW; pad ^= 0xfd) data.push(pad);
  const numLong = dataCW % numBlocks,
    shortLen = Math.floor(dataCW / numBlocks);
  const blocks: number[][] = [],
    eccs: number[][] = [];
  let off = 0;
  for (let i = 0; i < numBlocks; i++) {
    const len = shortLen + (i < numBlocks - numLong ? 0 : 1);
    const blk = data.slice(off, off + len);
    off += len;
    blocks.push(blk);
    eccs.push(rsRemainder(blk, ecLen));
  }
  const all: number[] = [];
  for (let i = 0; i <= shortLen; i++) for (const blk of blocks) if (i < blk.length) all.push(blk[i]);
  for (let i = 0; i < ecLen; i++) for (const e of eccs) all.push(e[i]);
  const size = version * 4 + 17;
  const mod = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const fun = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  const set = (x: number, y: number, dark: boolean) => {
    mod[y][x] = dark;
    fun[y][x] = true;
  };
  for (let i = 0; i < size; i++) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }
  const finder = (cx: number, cy: number) => {
    for (let dy = -4; dy <= 4; dy++)
      for (let dx = -4; dx <= 4; dx++) {
        const x = cx + dx,
          y = cy + dy;
        if (x < 0 || x >= size || y < 0 || y >= size) continue;
        const d = Math.max(Math.abs(dx), Math.abs(dy));
        set(x, y, d !== 2 && d !== 4);
      }
  };
  finder(3, 3);
  finder(size - 4, 3);
  finder(3, size - 4);
  for (const cy of ALIGN[version])
    for (const cx of ALIGN[version]) {
      if ((cx <= 8 && cy <= 8) || (cx <= 8 && cy >= size - 9) || (cx >= size - 9 && cy <= 8))
        continue;
      for (let dy = -2; dy <= 2; dy++)
        for (let dx = -2; dx <= 2; dx++)
          set(cx + dx, cy + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
    }
  for (let i = 0; i < 9; i++) {
    if (i !== 6) {
      set(8, i, false);
      set(i, 8, false);
    }
  }
  for (let i = 0; i < 8; i++) {
    set(size - 1 - i, 8, false);
    set(8, size - 1 - i, false);
  }
  set(8, 8, false);
  set(8, size - 8, true);
  if (version >= 7) {
    let rem = version;
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const vbits = (version << 12) | rem;
    for (let i = 0; i < 18; i++) {
      const bit = ((vbits >>> i) & 1) === 1;
      const a = size - 11 + (i % 3),
        b = Math.floor(i / 3);
      set(a, b, bit);
      set(b, a, bit);
    }
  }
  let i = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j;
        const y = ((right + 1) & 2) === 0 ? size - 1 - vert : vert;
        if (!fun[y][x] && i < all.length * 8) {
          mod[y][x] = ((all[i >>> 3] >>> (7 - (i & 7))) & 1) === 1;
          i++;
        }
      }
    }
  }
  const applyMask = (m: number) => {
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) if (!fun[y][x] && MASKS[m](x, y)) mod[y][x] = !mod[y][x];
  };
  const drawFormat = (m: number) => {
    let rem = m;
    for (let k = 0; k < 10; k++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const f = ((m << 10) | rem) ^ 0x5412;
    const bit = (k: number) => ((f >>> k) & 1) === 1;
    for (let k = 0; k <= 5; k++) set(8, k, bit(k));
    set(8, 7, bit(6));
    set(8, 8, bit(7));
    set(7, 8, bit(8));
    for (let k = 9; k < 15; k++) set(14 - k, 8, bit(k));
    for (let k = 0; k < 8; k++) set(size - 1 - k, 8, bit(k));
    for (let k = 8; k < 15; k++) set(8, size - 15 + k, bit(k));
    set(8, size - 8, true);
  };
  const penalty = () => {
    let score = 0;
    const runScore = (line: boolean[]) => {
      let s = 0,
        run = 1;
      for (let k = 1; k <= line.length; k++) {
        if (k < line.length && line[k] === line[k - 1]) run++;
        else {
          if (run >= 5) s += 3 + run - 5;
          run = 1;
        }
      }
      return s;
    };
    const P1 = [true, false, true, true, true, false, true, false, false, false, false];
    const P2 = [...P1].reverse();
    const patScore = (line: boolean[]) => {
      let s = 0;
      for (let k = 0; k + 11 <= line.length; k++) {
        let m1 = true,
          m2 = true;
        for (let t = 0; t < 11; t++) {
          if (line[k + t] !== P1[t]) m1 = false;
          if (line[k + t] !== P2[t]) m2 = false;
        }
        if (m1) s += 40;
        if (m2) s += 40;
      }
      return s;
    };
    for (let y = 0; y < size; y++) {
      const row = mod[y],
        col = mod.map((r) => r[y]);
      score += runScore(row) + runScore(col) + patScore(row) + patScore(col);
    }
    for (let y = 0; y < size - 1; y++)
      for (let x = 0; x < size - 1; x++) {
        const c = mod[y][x];
        if (c === mod[y][x + 1] && c === mod[y + 1][x] && c === mod[y + 1][x + 1]) score += 3;
      }
    let dark = 0;
    for (const row of mod) for (const c of row) if (c) dark++;
    score += Math.floor(Math.abs(dark * 20 - size * size * 10) / (size * size)) * 10;
    return score;
  };
  let mask = 0,
    best = Infinity;
  for (let m = 0; m < 8; m++) {
    applyMask(m);
    drawFormat(m);
    const p = penalty();
    if (p < best) {
      best = p;
      mask = m;
    }
    applyMask(m);
  }
  applyMask(mask);
  drawFormat(mask);
  return { size, modules: mod };
}

export function drawQR(canvas: HTMLCanvasElement, text: string): void {
  const { size, modules } = encode(text);
  const scale = 4,
    quiet = 4;
  const px = (size + quiet * 2) * scale;
  canvas.width = px;
  canvas.height = px;
  canvas.style.width = canvas.style.height = Math.round(px / 1) + "px";
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, px, px);
  ctx.fillStyle = "#1f2430";
  for (let y = 0; y < size; y++)
    for (let x = 0; x < size; x++)
      if (modules[y][x]) ctx.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
}
